import { ErrorPolicy, useMutation, useReactiveVar } from '@apollo/client';
import { CREATE_ACCEPTANCE } from '@mutations/createAcceptance';
import { CREATE_ACCEPTANCES } from '@mutations/createAcceptances';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { GET_APPLICATION_OPEN_SCREEN } from 'graphql/queries/getApplicationUpdateInfo';
import { GET_CUSTOMER } from 'graphql/queries/getCustomer';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import { useContext, useMemo } from 'react';
import {
  featureCountryCode,
  onboardingCompleted,
} from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import {
  Acceptance,
  AcceptanceType,
  CountryCode,
  Mutation,
  MutationCreateAcceptancesV3Args,
  MutationCreateAcceptanceV3Args,
} from 'types/generatedSchemaTypes';
import { isNewAppUser } from 'utils/accessTokenUtils';
import { deviceOs, isWeb } from 'utils/deviceUtils';
import Logger from 'utils/Logger';

const useCreateAcceptanceMutation = () => {
  const { navigateToOnboarding } = useAfterAuthenticationRedirect();
  const {
    refetchCustomer: fetchCustomerAfterSignUp,
    customer: { countryCode: customerCountryCode, acceptances, accountType },
  } = useContext(AccountContext);

  const featureCountry = useReactiveVar(featureCountryCode);
  const isNewUser = isNewAppUser(
    useAuthStore.getState().accessToken ?? '',
    !!acceptances?.length,
    accountType ?? 'NEW'
  );
  const { isLoggedIn } = useIsLoggedIn();

  const [createAcceptanceMutation, { loading: loadingAcceptance }] =
    useMutation<Mutation, MutationCreateAcceptanceV3Args>(CREATE_ACCEPTANCE, {
      errorPolicy: 'all',
    });

  const [
    createAcceptancesMutation,
    { loading: loadingAcceptances, error: createAcceptancesError },
  ] = useMutation<Mutation, MutationCreateAcceptancesV3Args>(
    CREATE_ACCEPTANCES,
    {
      errorPolicy: 'all',
    }
  );

  const loading = useMemo(
    () => loadingAcceptance || loadingAcceptances,
    [loadingAcceptance, loadingAcceptances]
  );

  const mutationOptions = (
    refetchCustomer: boolean,
    refetchAppOpenScreen: boolean,
    callback?: VoidFunction
  ) => ({
    awaitRefetchQueries: refetchCustomer,
    refetchQueries: refetchCustomer
      ? [
          {
            query: GET_CUSTOMER,
            errorPolicy: 'all' as ErrorPolicy,
          },
          ...(refetchAppOpenScreen
            ? [
                {
                  query: GET_APPLICATION_OPEN_SCREEN,
                  variables: { deviceOs, countryCode: featureCountry },
                },
              ]
            : []),
        ]
      : [],
    onError: () => {
      if (refetchAppOpenScreen) {
        showToast('error.genericError', 'error');
      }
    },
    onCompleted: callback,
  });

  const createAcceptance = async (
    accepted: boolean,
    type: AcceptanceType,
    countryCode: CountryCode | undefined,
    refetchCustomer = true,
    refetchAppOpenScreen = false,
    callback?: VoidFunction
  ) => {
    if (countryCode) {
      return await createAcceptanceMutation({
        variables: {
          input: {
            accepted,
            countryCode,
            type,
          },
        },
        ...mutationOptions(refetchCustomer, refetchAppOpenScreen, callback),
      });
    }
  };

  const handleAcceptances = async (marketingConsent: boolean) => {
    onboardingCompleted(!isNewUser);
    await createAcceptances(marketingConsent);
    await fetchCustomerAfterSignUp();
    if (isWeb) {
      navigateToOnboarding();
    }
  };

  const createAcceptances = async (
    marketingConsent: boolean,
    refetchCustomer = false,
    refetchAppOpenScreen = false,
    callback?: VoidFunction
  ) => {
    if (!featureCountry) {
      Logger.error(
        'CreateAcceptances can not be executed because feature country is null'
      );
      showToast('error.genericError', 'error');
      return;
    }

    if (!isLoggedIn) {
      Logger.error(
        'CreateAcceptances can not be executed because user is not logged in'
      );
      showToast('error.genericError', 'error');
      return;
    }

    const termsAcceptance: Acceptance = {
      accepted: true,
      type: 'TERMS_ACCEPTED',
      countryCode: featureCountry,
    };
    const marketingAcceptance: Acceptance = {
      accepted: marketingConsent,
      type: 'MARKETING_ALLOWED',
      countryCode: customerCountryCode as CountryCode,
    };
    return await createAcceptancesMutation({
      variables: {
        input: [termsAcceptance, marketingAcceptance],
      },
      ...mutationOptions(refetchCustomer, refetchAppOpenScreen, callback),
    });
  };

  return {
    createAcceptance,
    createAcceptances,
    handleAcceptances,
    loading,
  };
};
export default useCreateAcceptanceMutation;
